import axios from 'axios';
import { Module, ActionTree, MutationTree, GetterTree } from 'vuex';
import { MangoPayState, BankAccount, CreateBankAccountPayload, CreateMandatePayload, MangoPayMandate } from './types';
import { RootState } from '../types';
import { getCulture } from '@/utils/MangoPay';

const state: MangoPayState = {
  loadingMandates: false,
  loadingBankAccounts: false,
  mandates: {},
  bankAccounts: undefined,
};

const mutations: MutationTree<MangoPayState> = {
  // BANK ACCOUNTS
  SET_LOADING_ACCOUNTS(state: MangoPayState, loadingBankAccounts: Boolean) {
    state.loadingBankAccounts = loadingBankAccounts;
  },
  SET_BANK_ACCOUNTS(state: MangoPayState, bankAccounts: Array<BankAccount>) {
    if(bankAccounts){
      state.bankAccounts = bankAccounts.sort((a: BankAccount, b: BankAccount) => parseInt(b.CreationDate) - parseInt(a.CreationDate));
    }
  },
  ADD_BANK_ACCOUNT(state: MangoPayState, bankAccount: BankAccount) {
    state.bankAccounts.push(bankAccount);
  },

  // MANDATES
  SET_LOADING_MANDATES(state: MangoPayState, loadingMandates: Boolean) {
    state.loadingMandates = loadingMandates;
  },
  SET_BANK_MANDATE(state: MangoPayState, mandate: MangoPayMandate) {
    state.mandates[mandate.BankAccountId] = mandate;
  },
};

const getters: GetterTree<MangoPayState, RootState> = {
  mandates: (state: MangoPayState) => state.mandates,
  bankAccountMandate: (state: MangoPayState) => accountId => {
    return state.mandates[accountId] || null;
  },
  bankAccounts: (state: MangoPayState) => !state.bankAccounts ? state.bankAccounts : state.bankAccounts.sort((a, b) => a.CreationDate >= b.CreationDate ? 0 : 1),
  activeBankAccounts: (state: MangoPayState) => !state.bankAccounts ? state.bankAccounts : state.bankAccounts.filter(bankAccount => bankAccount.Active === true),
  getBankAccountById: (state: MangoPayState) => accountId => {
    if (!state.bankAccounts) {
      return null;
    }

    return state.bankAccounts
      .find((bankAccount: BankAccount) => bankAccount.Id === accountId);
  },
  getMandateById: (state: MangoPayState) => mandateId => {
    return Object.keys(state.mandates)
      .map(mandate => state.mandates[mandate])
      .find((mandate: MangoPayMandate) => mandate.Id === mandateId);
  },
  loadingMandates: (state: MangoPayState) => state.loadingMandates,
  loadingBankAccounts: (state: MangoPayState) => state.loadingBankAccounts,
};

const actions: ActionTree<MangoPayState, RootState> = {
  fetchUserBankAccounts({ commit, dispatch, rootState }: { commit: Function, dispatch: Function, rootState: any }) {
    if (!rootState.profile.mangoPayUser) {
      return null;
    }

    commit('SET_LOADING_ACCOUNTS', true);
    const query = '?sort=creationdate:desc&per_page=20';

    return axios.get(`${process.env.VUE_APP_EXPORO_WALLET_API_URL}/bank-account${query}`)
      .then(async response => {

        const accounts = response.data;
        const bankNamesQueue = [];
        const accountsQueue = [];

        await accounts.map(async (account: BankAccount) => {
          await axios.get(`${process.env.VUE_APP_IBAN_VALIDATOR}/${account.IBAN}?getBIC=true`)
            .then(({ data }) => {
              account.title = data.bankData.name;

              return data;
            });
          bankNamesQueue.push(dispatch('fetchBankAccountMandates', account.Id));
        }
        );

        accounts.map((account) =>
          accountsQueue.push(dispatch('fetchBankAccountMandates', account.Id))
        );

        await Promise.all(accountsQueue);

        commit('SET_BANK_ACCOUNTS', accounts);

        return Promise.resolve(accounts);
      })
      .catch(error => {
        commit('SET_BANK_ACCOUNTS', null);
        const err = error && error.response ? error.response : error;

        return Promise.reject(err);
      })
      .finally(() => {
        commit('SET_LOADING_ACCOUNTS', false);
      });
  },

  createBankAccount({ commit }: { commit: Function }, payload: CreateBankAccountPayload) {
    if (!payload) {
      return Promise.reject('no payload provided');
    }

    return axios.post(`${process.env.VUE_APP_EXPORO_WALLET_API_URL}/bank-account/`, payload)
      .then(async response => {
        let account = response.data;
        await axios.get(`${process.env.VUE_APP_IBAN_VALIDATOR}/${account.IBAN}?getBIC=true`)
          .then(({ data }) => {
            account.title = data.bankData.name;

            return data;
          });

        commit('ADD_BANK_ACCOUNT', account);

        return Promise.resolve(account);
      });
  },

  fetchBankAccountMandates({ commit, dispatch }: { commit: Function, dispatch: Function }, bankAccountId: number) {
    if (!bankAccountId) {
      return Promise.reject('no bankAccountId provided!');
    }
    commit('SET_LOADING_MANDATES', true);

    let query = '?sort=creationdate:desc&per_page=1';

    // add when fixed on BE
    // query += '&status=CREATED,SUBMITTED,ACTIVE';

    return axios.get(`${process.env.VUE_APP_EXPORO_WALLET_API_URL}/bank-accounts/${bankAccountId}/mandates${query}`)
      .then(async ({ data }) => {

        const mandatesQueue = [];

        data.map((mandate: MangoPayMandate) => {
          mandatesQueue.push(dispatch('fetchMandateDetails', mandate.Id));
        });

        const mandates = await Promise.all(mandatesQueue);

        if (mandates.length > 0) {
          commit('SET_BANK_MANDATE', mandates[0]);
        }

        return Promise.resolve(mandates);
      })
      .finally(() => {
        commit('SET_LOADING_MANDATES', false);
      });
  },

  fetchMandateDetails({ commit }: { commit: Function }, mandateId: number) {
    return axios.get(`${process.env.VUE_APP_EXPORO_WALLET_API_URL}/mandates/${mandateId}`)
      .then(response => Promise.resolve(response.data))
      .catch(error => Promise.reject(error.response));
  },

  createMandate({ rootState }: { rootState: any }, bankAccountId: string) {
    const payload: CreateMandatePayload = {
      BankAccountId: bankAccountId,
      ReturnURL: window.location.href.replace('/create-mandate', ''),
      Culture: getCulture(rootState.profile.exporoUser.naturalUser.citizenship),
    };

    return axios.post(`${process.env.VUE_APP_EXPORO_WALLET_API_URL}/mandates`, payload)
      .then(response => Promise.resolve(response.data))
      .catch(error => Promise.reject(error.response));
  },

  deleteMandate({ state }: { state: MangoPayState }, mandateId: string) {
    return axios.delete(`${process.env.VUE_APP_EXPORO_WALLET_API_URL}/mandates/${mandateId}`)
      .then(response => Promise.resolve(response.data))
      .catch(error => Promise.reject(error.response));
  },
};

export const mangoPay: Module<MangoPayState, RootState> = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
